import React, { useState, useEffect } from 'react';
import { MdChevronLeft, MdChevronRight } from "react-icons/md";

interface CarouselProps {
    images: { src: string; link?: string }[];
}

export const Carousel: React.FC<CarouselProps> = ({ images }) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    // Auto-scroll effect
    useEffect(() => {
        if (!images || images.length <= 1) return;

        const interval = setInterval(() => {
            setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
        }, 30000); // 5 seconds

        return () => clearInterval(interval);
    }, [images]);

    if (!images || images.length === 0) {
        return null;
    }

    const prevSlide = () => {
        const isFirstSlide = currentIndex === 0;
        const newIndex = isFirstSlide ? images.length - 1 : currentIndex - 1;
        setCurrentIndex(newIndex);
    };

    const nextSlide = () => {
        const isLastSlide = currentIndex === images.length - 1;
        const newIndex = isLastSlide ? 0 : currentIndex + 1;
        setCurrentIndex(newIndex);
    };

    const goToSlide = (slideIndex: number) => {
        setCurrentIndex(slideIndex);
    };

    const handleImageClick = () => {
        const currentImage = images[currentIndex];
        if (currentImage.link) {
            window.open(currentImage.link, '_blank');
        }
    };

    return (
        <div className='max-w-[1400px] w-full m-auto relative group'> 
            {/* Image Container */}
            <div className={`w-full flex justify-center relative transition-all duration-500 ease-in-out ${images[currentIndex].link ? 'cursor-pointer' : ''}`} onClick={handleImageClick}>
                <img
                    src={images[currentIndex].src}
                    alt="Campaign Slide"
                    className="w-auto h-auto max-h-[60vh] object-contain rounded-2xl"
                />
            </div>

            {/* Left Arrow */}
            {images.length > 1 && (
                <div className='hidden group-hover:block absolute top-[50%] -translate-x-0 translate-y-[-50%] left-5 text-2xl rounded-full p-2 bg-black/20 text-white cursor-pointer hover:bg-black/40 transition-colors'>
                    <MdChevronLeft onClick={prevSlide} size={30} />
                </div>
            )}

            {/* Right Arrow */}
            {images.length > 1 && (
                <div className='hidden group-hover:block absolute top-[50%] -translate-x-0 translate-y-[-50%] right-5 text-2xl rounded-full p-2 bg-black/20 text-white cursor-pointer hover:bg-black/40 transition-colors'>
                    <MdChevronRight onClick={nextSlide} size={30} />
                </div>
            )}

            {/* Dots */}
            {images.length > 1 && (
                <div className='flex justify-center py-2 gap-2 mt-2 absolute bottom-4 left-0 right-0'>
                     {images.map((_, slideIndex) => (
                        <div
                            key={slideIndex}
                            onClick={() => goToSlide(slideIndex)}
                            className={`cursor-pointer transition-all duration-300 w-3 h-3 rounded-full ${currentIndex === slideIndex ? 'bg-primary scale-110' : 'bg-gray-300'}`}
                        >
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
